import { useDispatch } from 'react-redux'
import { TableList } from '../../Components/TableList/TableList'
import { type Company, type CompanyAccidentCount } from '../../Store/DataSlice'
import { setDestinationView } from '../../Store/InterfaceSlice'

interface CompaniesTableProps {
  companies: Company[]
  isLoading: boolean
  showAdminColumns?: boolean
}

export function CompaniesTable({
  companies,
  isLoading,
  showAdminColumns,
}: CompaniesTableProps) {
  const dispatch = useDispatch()

  return (
    <TableList
      items={companies}
      configurations={{
        vatNumber: {
          label: 'Ondernemingsnummer',
          show: true,
        },
        name: {
          label: 'Naam',
          show: true,
        },
        legalForm: {
          label: 'Rechtsvorm',
          show: true,
        },
        email: {
          label: 'Email',
          show: false,
        },
        website: {
          label: 'Website',
          show: false,
        },
        addressLine1: {
          label: 'Straat en huisnummer',
          show: false,
        },
        postalCode: {
          label: 'Postcode',
          show: false,
        },
        city: {
          label: 'Gemeente',
          show: false,
        },
        countryCode: {
          label: 'Land',
          show: false,
        },
        naceCode: {
          label: 'NACE Code',
          show: false,
        },
        branches: {
          label: 'Vestigingen',
          show: false,
          getValue: (value) => (value as string[])?.join(', '),
        },
        accidentCounts: {
          label: 'Ongevallen cijfers',
          show: false,
          getValue: (value) => {
            const accidentCount = [...(value as CompanyAccidentCount[])]
            if (!accidentCount?.length) return ''
            const lastAccidentCount = accidentCount?.sort(
              (a, b) => a.year - b.year
            )[0]
            return `${lastAccidentCount.year}: ${lastAccidentCount.count}`
          },
        },
        lastKpbCheck: {
          label: 'Laatste Kbp check',
          show: false,
        },
        lastKpbUpdate: {
          label: 'Laatste Kbp update',
          show: showAdminColumns ?? false,
          getValue: (_value, key) => {
            const company = companies.find((c) => c.vatNumber === key)
            if (company == null || !isBelgianVat(company.vatNumber))
              return 'nvt'
            const lastKpbUpdate =
              company.lastKpbUpdate != null
                ? ` (${new Date(company.lastKpbUpdate).toLocaleDateString()})`
                : ''
            return (
              (company.lastKpbCheckSuccess === true ? 'success' : 'failure') +
              lastKpbUpdate
            )
          },
        },
        lastKpbCheckSuccess: {
          label: 'Laatste Kbp check success',
          show: false,
          getValue: (value) =>
            (value as boolean | undefined) === true ? 'success' : 'failure',
        },
      }}
      localizationSection={'company'}
      getUniqueKey={(item) => item.vatNumber}
      onRowClick={(item) =>
        dispatch(
          setDestinationView({
            destinationView: 'company-detail',
            detailKey: item.vatNumber,
          })
        )
      }
      isLoading={isLoading}
    />
  )
}

function isBelgianVat(vatNumber: string) {
  return (
    vatNumber != null &&
    vatNumber.length >= 2 &&
    vatNumber.substring(0, 2) === 'BE'
  )
}
